import { t } from '@lingui/core/macro';
import { useWindowSize } from 'react-use';
import { Resource, Site } from '~types/commonTypes';
import { CapabilityStateMapping } from '~constants/initialData';
import { breakpoints } from '~constants/theme';

// NOTE: This is just an example
export const times = (timesArg: number) => {
  return (func: (index: number) => any) => {
    return Array(timesArg)
      .fill('-')
      .map((_, index) => {
        return func(index);
      });
  };
};

export const scrollToBottom = () => {
  window.scrollTo(0, document.body.scrollHeight);
};

export const range = (n: number) => Array.from({ length: n }, (x, i) => i);

export const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

export const randomNumBetween = (min: number, max: number) =>
  Math.floor(Math.random() * max) + min;

export const isNumeric = (n: any) => !isNaN(parseFloat(n)) && isFinite(n);

/* -------------------------------------------------------------------------- */
/* Small native collection / string / function helpers                       */
/* -------------------------------------------------------------------------- */

// Random integer between `lower` and `upper`, both inclusive.
export const random = (lower: number, upper: number) =>
  lower + Math.floor(Math.random() * (upper - lower + 1));

export const sum = (values: number[] | null | undefined) =>
  (values ?? []).reduce((acc, v) => acc + v, 0);

export const mean = (values: number[] | null | undefined) =>
  values && values.length ? sum(values) / values.length : NaN;

export const min = (values: number[] | null | undefined) =>
  values && values.length ? Math.min(...values) : undefined;

export const max = (values: number[] | null | undefined) =>
  values && values.length ? Math.max(...values) : undefined;

// Group items by the key returned by `iteratee`. Nullish input -> {}.
export function groupBy<T>(
  collection: T[] | null | undefined,
  iteratee: (item: T) => PropertyKey
): Record<string, T[]> {
  const result: Record<string, T[]> = {};
  for (const item of collection ?? []) {
    const key = iteratee(item) as string;
    (result[key] ??= []).push(item);
  }
  return result;
}

type Order = 'asc' | 'desc';
type Iteratee<T> = ((item: T) => any) | keyof T;

// Stable multi-key sort; returns a new array. Nullish input -> [].
export function orderBy<T>(
  collection: T[] | null | undefined,
  iteratees: Iteratee<T> | Iteratee<T>[],
  orders?: Order | Order[]
): T[] {
  const its = Array.isArray(iteratees) ? iteratees : [iteratees];
  const ords = Array.isArray(orders) ? orders : orders ? [orders] : [];
  const valueOf = (item: T, it: Iteratee<T>) =>
    typeof it === 'function' ? it(item) : item?.[it];
  return [...(collection ?? [])].sort((a, b) => {
    for (let i = 0; i < its.length; i++) {
      const order = ords[i] ?? 'asc';
      const av = valueOf(a, its[i]);
      const bv = valueOf(b, its[i]);
      if (av < bv) return order === 'desc' ? 1 : -1;
      if (av > bv) return order === 'desc' ? -1 : 1;
    }
    return 0;
  });
}

const reApostrophe = /['’]/g;
const reWords =
  /[A-Z\xc0-\xd6\xd8-\xde]+(?=[A-Z\xc0-\xd6\xd8-\xde][a-z\xdf-\xf6\xf8-\xff]+)|[A-Z\xc0-\xd6\xd8-\xde]?[a-z\xdf-\xf6\xf8-\xff]+|[A-Z\xc0-\xd6\xd8-\xde]+|\d+/g;

// camelCase a string. Handles the uppercase capability names used as state
// keys, e.g. "AIR_HEAT_PUMP" -> "airHeatPump", "CO2" -> "co2".
export const camelCase = (string?: string | null) => {
  const words =
    String(string ?? '')
      .replace(reApostrophe, '')
      .match(reWords) || [];
  return words
    .map((word, index) => {
      const lower = word.toLowerCase();
      return index === 0
        ? lower
        : lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join('');
};

// Debounce supporting `leading`/`trailing` options.
export function debounce<A extends any[]>(
  fn: (...args: A) => unknown,
  wait: number,
  {
    leading = false,
    trailing = true,
  }: { leading?: boolean; trailing?: boolean } = {}
) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: A | null = null;
  function debounced(this: any, ...args: A) {
    lastArgs = args;
    const callNow = leading && timer === null;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      if (trailing && lastArgs) fn.apply(this, lastArgs);
      lastArgs = null;
    }, wait);
    if (callNow) fn.apply(this, args);
  }
  debounced.cancel = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    lastArgs = null;
  };
  return debounced;
}

export const truncate = (str: string, len: number) => {
  if (str.length > len) return `${str.substring(0, len - 3)}...`;
  return str;
};

export const truncateFromStart = (str: string, len: number) => {
  if (str.length > len) return `...${str.substring(str.length - len)}`;
  return str;
};

export const truncatePathFromStart = (str: string, len: number) => {
  if (str.length > len)
    return `...${str
      .substring(str.length - len)
      .split('/')
      .slice(1)
      .join('/')}`;

  return str;
};

export const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export const noop = () => {};

// eslint-disable-next-line
export const noopWithArgs = (...args: any[]) => {};

type RGB = {
  r: number;
  g: number;
  b: number;
};

const hexToRgb = (hex: string): RGB | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

export const fadeColor = (color: string, fade: number) => {
  const { r, g, b } = hexToRgb(color) as RGB;
  return `rgba(${r}, ${g}, ${b}, ${clamp(fade, 0, 1)})`;
};

export const iterate = (obj: any) => {
  Object.keys(obj).forEach(key => {
    if (
      typeof obj[key] === 'object' &&
      obj[key] !== null &&
      key === 'children'
    ) {
      iterate(obj[key]);
    }
  });
};

/**
 * Helper function for `Array.filter()` for filtering away undefined items while
 * narrowing typing from `(T | undefined)[]` to `T[]`
 */
export function filterUndefined<T>(item: T | undefined): item is T {
  return item !== undefined;
}

/**
 * Helper function for `Array.filter()` for filtering away undefined items while
 * narrowing typing from `(T | undefined)[]` to `T[]`
 */
export function filterNull<T>(item: T | null): item is T {
  return item !== null;
}

export function filterFalse<T>(item: T | false): item is T {
  return item !== false;
}

export function isSite(item: Resource | Site | undefined): item is Site {
  return item?.resourceType === 'Cozify.Sites';
}

export function formatDateString(time?: number): string {
  if (!time) return '';
  const date = new Date(time);
  const dateStr =
    ('00' + date.getDate()).slice(-2) +
    '.' +
    ('00' + (date.getMonth() + 1)).slice(-2) +
    '.' +
    date.getFullYear() +
    ' ' +
    ('00' + date.getHours()).slice(-2) +
    ':' +
    ('00' + date.getMinutes()).slice(-2);
  return dateStr;
}

export function formatTimeString(timestamp: number, seconds?: boolean): string {
  const date = new Date(timestamp);
  const dateStr =
    ('00' + date.getHours()).slice(-2) +
    ':' +
    ('00' + date.getMinutes()).slice(-2) +
    (seconds ? ':' + ('00' + date.getSeconds()).slice(-2) : '');
  return dateStr;
}

export const useIsMobile = () => {
  return useWindowSize().width < breakpoints.phone.max;
};

export const formatValue = ({
  value,
  options,
  unit,
  rounded,
}: {
  value: string | number | boolean | object;
  options?: {
    scale?: number;
    format?: string;
    formatToDate?: boolean;
    formatToHexDump?: boolean;
    decimals?: number;
  };
  unit?: CapabilityStateMapping['unit'];
  rounded?: boolean;
}): string | number => {
  let formattedValue = '-';
  if (value == null) {
    return formattedValue;
  }
  if (typeof value === 'number' || typeof value === 'string') {
    const { scale = 1, formatToDate, format, decimals } = options || {};
    const isDate = !!formatToDate || format === 'datetime';
    if (isDate) {
      formattedValue = new Date(Number(value)).toLocaleString('fi-FI');
    } else if (typeof value === 'number') {
      const scaled = value * scale;
      // A field that declares `decimals` is shown with exactly that many
      // fractional digits (e.g. battery voltage always as "3.50"); otherwise
      // fall back to the generic round-to-2 (which drops trailing zeros).
      formattedValue =
        decimals != null
          ? scaled.toFixed(decimals)
          : (rounded
              ? Math.round(Math.round((scaled + Number.EPSILON) * 100) / 100)
              : Math.round((scaled + Number.EPSILON) * 100) / 100
            ).toString();
    } else if (typeof value === 'string') {
      // Plain (non-date) string values render as-is rather than the "-" default.
      formattedValue = value;
    }
    // TODO: Add support for finnish unit
    if (unit?.en && formattedValue && !isDate)
      formattedValue = `${formattedValue} ${unit.en}`;
  } else if (typeof value === 'boolean') {
    formattedValue = value ? t`True` : t`False`;
  } else if (Array.isArray(value)) {
    const { formatToHexDump, format } = options || {};
    if (formatToHexDump || format === 'hex') {
      try {
        formattedValue =
          'len=' +
          value.length.toString(10) +
          ' [ ' +
          value
            .map(x => '0x' + x.toString(16).toUpperCase().padStart(2, '0'))
            .join(', ') +
          ' ]';
      } catch {
        formattedValue = value.map(x => x.toString()).join(', ');
      }
    } else {
      formattedValue = value.map(x => x.toString()).join(', ');
    }
    if (!formattedValue || formattedValue[formattedValue.length - 1] !== ']') {
      formattedValue = '[' + formattedValue + ']';
    }
  } else {
    try {
      formattedValue = value.toString();
    } catch (e) {
      formattedValue = '-';
    }
  }

  return formattedValue;
};

export function validateEmail(input: string) {
  const validRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g;

  if (input.match(validRegex)) {
    return true;
  } else {
    return false;
  }
}
